import { AssetBanner } from "@/components/pharos/art";
import {
  VEHICLES,
  ASSET_CLASSES,
  SECTORS,
  REGIONS,
  SIZE_BANDS,
  HIGHLIGHTS,
  labelFor,
  countryName,
} from "@/components/pharos/constants";

// Money with the deal's own currency; compact so 50,000,000 reads "$50M".
export function formatMoney(value, currency) {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(n);
  } catch {
    return String(n);
  }
}

// closed / raise as a whole percent, clamped to 0..100 (bad input -> 0).
export function progressPct(closed, raise) {
  const c = Number(closed);
  const r = Number(raise);
  if (!Number.isFinite(c) || !Number.isFinite(r) || r <= 0 || c <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((c / r) * 100)));
}

// "YYYY-MM-DD" is a calendar date, not an instant: compare local midnights so
// the label never flips with the viewer's UTC offset.
export function countdownLabel(closeDate, now = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(closeDate || "");
  if (!m) return "";
  const close = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((close - today) / 86_400_000);
  if (days < 0) return "Closed";
  if (days === 0) return "Closes today";
  return days === 1 ? "1 day left" : `${days} days left`;
}

function hasValue(v) {
  return v !== null && v !== undefined && v !== "";
}

function Pill({ children, className = "bg-gray-100 text-gray-700 border-gray-200" }) {
  return (
    <span className={"inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium " + className}>
      {children}
    </span>
  );
}

function Figure({ label, value }) {
  return (
    <div>
      <p className="text-base font-semibold text-gray-900 tabular-nums">{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
    </div>
  );
}

// One-page deal teaser. Renders ONLY structured fields from the public deal
// shape (never owner/seller data, even if a row carries it). `children` is a
// footer slot for the caller's actions; `large` enlarges it for the detail
// dialog.
export default function DealCard({ deal, large = false, children }) {
  if (!deal) return null;
  const cur = deal.currency;
  const pct = progressPct(deal.amount_closed, deal.total_raise);
  const countdown = countdownLabel(deal.close_date);
  const sectors = (deal.sectors || []).map((s) => labelFor(SECTORS, s)).filter(Boolean);
  const highlights = (deal.highlights || []).map((h) => labelFor(HIGHLIGHTS, h)).filter(Boolean);
  const track = Array.isArray(deal.track_record) ? deal.track_record : [];

  const horizon =
    hasValue(deal.horizon_min_years) && hasValue(deal.horizon_max_years)
      ? `${deal.horizon_min_years}-${deal.horizon_max_years} yrs`
      : hasValue(deal.horizon_min_years)
        ? `${deal.horizon_min_years}+ yrs`
        : hasValue(deal.horizon_max_years)
          ? `up to ${deal.horizon_max_years} yrs`
          : "";

  const figures = [
    { label: "Raise", value: formatMoney(deal.total_raise, cur) },
    { label: "Min ticket", value: formatMoney(deal.min_ticket, cur) },
    { label: "Target IRR", value: hasValue(deal.target_irr) ? `${deal.target_irr}%` : "" },
    { label: "Target MOIC", value: hasValue(deal.target_moic) ? `${deal.target_moic}x` : "" },
    { label: "Horizon", value: horizon },
    { label: "Mgmt fee", value: hasValue(deal.mgmt_fee_pct) ? `${deal.mgmt_fee_pct}%` : "" },
    { label: "Carry", value: hasValue(deal.carry_pct) ? `${deal.carry_pct}%` : "" },
    { label: "Access fee", value: hasValue(deal.access_fee_pct) ? `${deal.access_fee_pct}%` : "" },
  ].filter((f) => f.value);

  const facts = [
    labelFor(VEHICLES, deal.vehicle),
    labelFor(ASSET_CLASSES, deal.asset_class),
    labelFor(REGIONS, deal.geography),
    ...(Array.isArray(deal.countries) ? deal.countries : []).map((c) => countryName(c)).filter(Boolean),
    cur,
    labelFor(SIZE_BANDS, deal.size_band),
    ...sectors,
  ].filter(Boolean);

  return (
    <div className="flex flex-col h-full overflow-hidden rounded-xl border bg-white shadow-sm" data-testid="deal-card">
      <AssetBanner assetClass={deal.asset_class} className={large ? "w-full h-56" : "w-full h-40"}>
        <div className="h-full flex flex-col justify-between p-3">
          <div className="flex items-start justify-between gap-2">
            <span />
            {countdown && (
              <span className="rounded-full bg-black/40 px-2 py-0.5 text-[11px] font-medium text-white">
                {countdown}
              </span>
            )}
          </div>
          <h3
            className={(large ? "text-2xl" : "text-lg") + " font-semibold text-white leading-tight [text-shadow:0_1px_8px_rgba(0,0,0,0.55)]"}
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {deal.title}
          </h3>
        </div>
      </AssetBanner>

      <div className="flex-1 p-4 space-y-4">
        {facts.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {facts.map((f, i) => (
              <Pill key={`${f}-${i}`}>{f}</Pill>
            ))}
          </div>
        )}

        {figures.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {figures.map((f) => (
              <Figure key={f.label} label={f.label} value={f.value} />
            ))}
          </div>
        )}

        {highlights.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {highlights.map((h) => (
              <Pill key={h} className="bg-red-50 text-red-700 border-red-100">
                {h}
              </Pill>
            ))}
          </div>
        )}

        {track.length > 0 && (
          <table className="w-full text-xs" aria-label="Track record">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="font-medium py-1">Fund</th>
                <th className="font-medium py-1">DPI</th>
                <th className="font-medium py-1">Net IRR</th>
              </tr>
            </thead>
            <tbody>
              {track.map((r, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="py-1">Fund {r.fund_number}</td>
                  <td className="py-1 tabular-nums">{r.dpi}x</td>
                  <td className="py-1 tabular-nums">{r.net_irr}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {hasValue(deal.total_raise) && (
          <div>
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>
                {formatMoney(deal.amount_closed || 0, cur)} of {formatMoney(deal.total_raise, cur)}
              </span>
              <span className="tabular-nums">{pct}%</span>
            </div>
            <div
              role="progressbar"
              aria-label="Amount closed"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
              className="h-2 w-full rounded-full bg-gray-100 overflow-hidden"
            >
              <div className="h-full rounded-full bg-[#C41E2A]" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
      </div>

      {children && <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3">{children}</div>}
    </div>
  );
}
